import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface PostalInvoiceSummaryLine {
  occasion: "INVOICE" | "NOTICE" | "MEETING_INVITE";
  letterCount: number;
  totalPages: number;
  grossCents: number;
  netCents: number;
  taxCents: number;
}

export interface PostalInvoice {
  id: number;
  number: string;
  amountGrossCents: number;
  amountNetCents: number;
  amountTaxCents: number;
  currency: string;
  status: "generating" | "open" | "paid" | "void" | "uncollectible";
  issuedAt: string;
  dueAt: string | null;
  periodStart: string;
  periodEnd: string;
  externalId: string | null;
  summary: PostalInvoiceSummaryLine[];
  association?: { id: number; name: string };
  pdf?: { id: string; filename: string; originalname: string } | null;
}

export interface LetterDispatchRow {
  id: number;
  occasion: "INVOICE" | "NOTICE" | "MEETING_INVITE";
  occasionId: number;
  pages: number;
  totalGrossCents: number;
  totalNetCents: number;
  totalTaxCents: number;
  sentAt: string;
  billedAt: string | null;
  recipientUser?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
  };
}

interface PostalInvoiceState {
  allInvoices: PostalInvoice[];
  byAssociation: PostalInvoice[];
  current: PostalInvoice | null;
  dispatches: {
    items: LetterDispatchRow[];
    total: number;
    limit: number;
    offset: number;
  } | null;
  status: "idle" | "loading" | "failed";
  markPaidError: string | null;
}

const initialState: PostalInvoiceState = {
  allInvoices: [],
  byAssociation: [],
  current: null,
  dispatches: null,
  status: "idle",
  markPaidError: null,
};

export const fetchAllPostalInvoices = createAsyncThunk<PostalInvoice[]>(
  "postalInvoice/fetchAll",
  async () => {
    const res = await axiosInstance.get<PostalInvoice[]>("/postal-invoices");
    return res.data;
  }
);

export const fetchPostalInvoicesByAssociation = createAsyncThunk<
  PostalInvoice[],
  number
>("postalInvoice/fetchByAssociation", async (associationId) => {
  const res = await axiosInstance.get<PostalInvoice[]>(
    `/postal-invoices/association/${associationId}`
  );
  return res.data;
});

export const fetchPostalInvoice = createAsyncThunk<PostalInvoice, number>(
  "postalInvoice/fetchOne",
  async (id) => {
    const res = await axiosInstance.get<PostalInvoice>(
      `/postal-invoices/${id}`
    );
    return res.data;
  }
);

export const fetchPostalInvoiceDispatches = createAsyncThunk<
  PostalInvoiceState["dispatches"],
  { id: number; limit?: number; offset?: number }
>("postalInvoice/fetchDispatches", async ({ id, limit, offset }) => {
  const res = await axiosInstance.get(
    `/postal-invoices/${id}/letter-dispatches`,
    { params: { limit, offset } }
  );
  return res.data;
});

export const markPostalInvoicePaid = createAsyncThunk<
  PostalInvoice,
  number,
  { rejectValue: { message: string } }
>("postalInvoice/markPaid", async (id, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.put<PostalInvoice>(
      `/postal-invoices/${id}/mark-paid`
    );
    await dispatch(fetchAllPostalInvoices());
    return res.data;
  } catch (err: any) {
    const message: string =
      err?.response?.data?.message ?? err?.message ?? "Virhe merkitsemisessä";
    return rejectWithValue({ message });
  }
});

export const regeneratePostalInvoicePdf = createAsyncThunk<
  PostalInvoice,
  number,
  { rejectValue: { message: string } }
>("postalInvoice/regeneratePdf", async (id, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post<PostalInvoice>(
      `/postal-invoices/${id}/regenerate-pdf`
    );
    await dispatch(fetchAllPostalInvoices());
    return res.data;
  } catch (err: any) {
    const message: string =
      err?.response?.data?.message ??
      err?.message ??
      "PDF:n muodostaminen epäonnistui";
    return rejectWithValue({ message });
  }
});

const slice = createSlice({
  name: "postalInvoice",
  initialState,
  reducers: {
    clearCurrent(state) {
      state.current = null;
      state.dispatches = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllPostalInvoices.pending, (s) => {
        s.status = "loading";
      })
      .addCase(
        fetchAllPostalInvoices.fulfilled,
        (s, a: PayloadAction<PostalInvoice[]>) => {
          s.allInvoices = a.payload;
          s.status = "idle";
        }
      )
      .addCase(fetchAllPostalInvoices.rejected, (s) => {
        s.status = "failed";
      })
      .addCase(
        fetchPostalInvoicesByAssociation.fulfilled,
        (s, a: PayloadAction<PostalInvoice[]>) => {
          s.byAssociation = a.payload;
        }
      )
      .addCase(
        fetchPostalInvoice.fulfilled,
        (s, a: PayloadAction<PostalInvoice>) => {
          s.current = a.payload;
        }
      )
      .addCase(fetchPostalInvoiceDispatches.fulfilled, (s, a) => {
        s.dispatches = a.payload;
      })
      .addCase(markPostalInvoicePaid.rejected, (s, a) => {
        s.markPaidError = a.payload?.message ?? "Virhe merkitsemisessä";
      })
      .addCase(markPostalInvoicePaid.fulfilled, (s) => {
        s.markPaidError = null;
      });
  },
});

export const { clearCurrent } = slice.actions;
export default slice.reducer;
